/**
 * `/overview` — WP-K4. The Executive and Manager dashboards, as a day sheet.
 *
 * `web/shapes/overview.md` is the shape file, committed before this code, and it
 * carries the decisions rather than this header repeating them. What is worth saying
 * here is what this file *composes*, and the four facts that shape it.
 *
 * ── The date is the axis ────────────────────────────────────────────────────
 *
 * The direction contract for this surface (`.impeccable/surfaces/web-src-app-tsx.md`)
 * is a page torn from a desk calendar: the day and its two sync stamps, then what is
 * wrong today, then the three figures, then who needs help. There is no chart above
 * the fold — the seven-day strip sits **below** the table, where a manager who has
 * already answered "is anything wrong today" goes looking for a shape over time.
 * `?date=` joins `?window=` and `?department_id=` in the query string so a flip is a
 * navigation and every drill-down under it carries the same day.
 *
 * ── One route, two personas, and no role check ──────────────────────────────
 *
 * Spec 01 §12 describes an executive dashboard and a manager dashboard. They are the
 * same regions over different populations, and **the server decides which** — a
 * `view_department` session gets a department's worth of rows because `resolveScope`
 * narrowed the SQL. Nothing here reads `SessionUser.role`; `web/src/nav/menu.ts`'s
 * boxed comment is the rule, and re-deriving a field of view in the browser would be
 * a second authorisation path that drifts the first time a grant row is written.
 *
 * The one thing the client *does* branch on is a permission it can see in the
 * effective set — `manage_devices` for the device strip — and it branches to decide
 * **whether to ask**, not whether to trust: the server refuses either way, and
 * calling an endpoint this session cannot reach would put a permission refusal on a
 * page that is otherwise working.
 *
 * ── Four reads, four freshness lives ────────────────────────────────────────
 *
 * Roster, day rollups, running timers, devices. Each is its own region with its own
 * skeleton and its own failure, because a single page-level spinner holds four
 * answers behind the slowest and a single page-level error reports one refusal as
 * though the whole dashboard were broken (`PRODUCT.md` §4).
 *
 * ── It does not compute today ───────────────────────────────────────────────
 *
 * `SessionUser.timezone` is the company's zone and `localDateIn` resolves "today" in
 * it, once, here — because today is a different day in Hanoi and in Berlin and every
 * window on this screen is measured back from it. `nowMs` is a parameter for the
 * same reason `AttendanceScreen` takes one: the interesting cases are edges. The
 * **sheet date** may be an earlier day chosen with the flip; today remains the
 * ceiling, because there is no rollup for tomorrow.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { useI18n } from '../i18n';
import { localDateIn } from '../i18n/datetime';
import {
  attendanceDrillDownPath,
  dailyAggregatePath,
  devicesPath,
  overviewRosterPath,
  runningTimersPath,
  taskCacheProbePath,
  TIMELINE_PAGE_LIMIT,
} from '../api/paths';
import type {
  AttendanceGridResponse,
  DailyAggregateListResponse,
  DeviceListResponse,
  TaskSearchResponse,
  TimeEntryListResponse,
} from '../api/types';
import { usePolledQuery } from '../api/usePolledQuery';
import { useSession } from '../auth/SessionProvider';
import {
  foldWindow,
  livenessCensus,
  notComputedKeys,
  parseWindow,
  presenceCensus,
  windowRange,
  type OverviewWindow,
} from '../overview/fold';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Skeleton } from '../states/Skeleton';
import { DaySheetHead } from './overview/DaySheetHead';
import { DeviceStrip } from './overview/DeviceStrip';
import { ExceptionList, type ExceptionKind } from './overview/ExceptionList';
import { FilterBar } from './overview/FilterBar';
import { HoursTile } from './overview/HoursTile';
import { AiSlot } from './overview/AiSlot';
import { PendingSlots } from './overview/PendingSlots';
import { PresenceTile } from './overview/PresenceTile';
import { RatioTile } from './overview/RatioTile';
import { ReportsTable, type ReportRow } from './overview/ReportsTable';
import { TaskDataTile, newestCachedAt } from './overview/TaskDataTile';
import { TrendChart } from './overview/TrendChart';

/**
 * The two poll intervals, and the reason there are two.
 *
 * `web/shapes/overview.md` §12. Task 36 writes an hour's aggregate row **once an
 * hour**, so a 30-second poll re-reads an unchanged row 119 times out of 120 and
 * buys nothing; five minutes bounds the visible lag behind an hour boundary at five
 * minutes, at twelve reads an hour per open dashboard. Running timers change when
 * somebody presses a key, not on a schedule, so tying them to the aggregate cadence
 * would show a manager a task that ended four minutes ago.
 *
 * `usePolledQuery` stops both while the tab is hidden. Spec 01 §12's acceptance
 * criterion is "refresh không tạo tải quá mức", and the cheapest request is the one
 * a tab nobody is looking at does not make.
 */
export const AGGREGATE_POLL_MS = 5 * 60_000;
export const LIVE_POLL_MS = 60_000;

/**
 * How many roster cells one page asks for.
 *
 * `GET /attendance` pages the `(employee × date)` grid **employee-major**, and this
 * screen asks for one date, so the limit is a number of employees. `total` on the
 * envelope is still the whole scope, which is why the headcount tile is right even
 * when the table below it is a page.
 */
const ROSTER_LIMIT = 200;
const TIMER_LIMIT = 200;
const DEVICE_LIMIT = 200;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * How far up the "who needs help" table a row is lifted.
 *
 * The table is titled *Ai cần giúp*, so it is ordered by need rather than by the
 * order the server happened to page rows in. The ranking is only over facts already
 * printed in the row's own cells — there is no hidden score here, and a reader can
 * check the order against the columns beside it.
 */
function needRank(row: ReportRow): number {
  const status = row.day.attendance_status;
  if (status === 'absent') return 0;
  if (status === 'late' || status === 'late_and_early') return 1;
  if (status === 'data_incomplete') return 2;
  if (row.presence === 'no_activity_data') return 3;
  if (row.running === undefined) return 4;
  return 5;
}

export interface OverviewScreenProps {
  readonly nowMs?: number;
}

export function OverviewScreen({ nowMs }: OverviewScreenProps = {}): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';
  const permissions = user?.permissions ?? [];
  const canReadDevices = permissions.includes('workforce.manage_devices');

  const todayLocal = useMemo(() => localDateIn(timezone, nowMs ?? Date.now()), [timezone, nowMs]);

  // The filter context lives in the URL, not in state — shape file §7. Every
  // drill-down below is built from these values, so a link cannot carry a
  // different population, or a different day, than the panel it sits under.
  const [params, setParams] = useSearchParams();
  const windowChoice = parseWindow(params.get('window'));
  const departmentId = params.get('department_id');
  const requestedDate = params.get('date');
  const today =
    requestedDate !== null && ISO_DAY.test(requestedDate) && requestedDate <= todayLocal
      ? requestedDate
      : todayLocal;
  const [reloadToken, setReloadToken] = useState(0);

  const setFilter = (next: {
    window?: OverviewWindow;
    departmentId?: string | null;
    date?: string;
  }): void => {
    const updated = new URLSearchParams(params);
    if (next.window !== undefined) updated.set('window', next.window);
    if (next.date !== undefined) {
      if (next.date === todayLocal) updated.delete('date');
      else updated.set('date', next.date);
    }
    if (next.departmentId !== undefined) {
      if (next.departmentId === null) updated.delete('department_id');
      else updated.set('department_id', next.departmentId);
    }
    setParams(updated, { replace: false });
  };

  const range = windowRange(windowChoice, today);
  const department = departmentId ?? undefined;

  const roster = usePolledQuery<AttendanceGridResponse>(
    overviewRosterPath(today, department, ROSTER_LIMIT),
    AGGREGATE_POLL_MS,
    reloadToken,
  );
  // `range` is two local dates, inclusive, in the company's zone; the endpoint's own
  // window is half-open and made of instants, and `dailyAggregatePath` converts. That
  // is why the zone is an argument: a window cut on UTC's midnight is a different
  // window, and a one-day window with an inclusive end is not a window at all (it was
  // refused `400 … "to must be after from"` on every load — QA-G-009).
  const windowDays = usePolledQuery<DailyAggregateListResponse>(
    dailyAggregatePath(range.from, range.to, timezone),
    AGGREGATE_POLL_MS,
    reloadToken,
  );
  // When the window *is* today the two reads are the same request, so the second is
  // not issued at all rather than asking the server the same question twice.
  const todayDays = usePolledQuery<DailyAggregateListResponse>(
    windowChoice === 'today' ? null : dailyAggregatePath(today, today, timezone),
    AGGREGATE_POLL_MS,
    reloadToken,
  );
  const timers = usePolledQuery<TimeEntryListResponse>(
    runningTimersPath(TIMER_LIMIT),
    LIVE_POLL_MS,
    reloadToken,
  );
  const tasks = usePolledQuery<TaskSearchResponse>(
    taskCacheProbePath(),
    AGGREGATE_POLL_MS,
    reloadToken,
  );
  const devices = usePolledQuery<DeviceListResponse>(
    canReadDevices ? devicesPath(DEVICE_LIMIT, department) : null,
    AGGREGATE_POLL_MS,
    reloadToken,
  );

  const fold = useMemo(
    () =>
      foldWindow(
        windowDays.data?.data ?? [],
        windowDays.data?.page.has_more ?? false,
        { from: range.from, to: range.to },
        windowDays.data?.coverage,
      ),
    [windowDays.data, range.from, range.to],
  );

  const todaySource = windowChoice === 'today' ? windowDays.data : todayDays.data;

  const census = useMemo(
    () =>
      presenceCensus({
        employeeIds: (roster.data?.data ?? []).map((row) => row.employee_id),
        todayRows: todaySource?.data ?? [],
        notComputed: notComputedKeys(todaySource?.coverage),
        today,
      }),
    [roster.data, todaySource, today],
  );

  const runningByEmployee = useMemo(() => {
    const map = new Map<string, (typeof entries)[number]>();
    const entries = timers.data?.data ?? [];
    for (const entry of entries) {
      if (!map.has(entry.employee_id)) map.set(entry.employee_id, entry);
    }
    return map;
  }, [timers.data]);

  const timersRead = timers.data !== null;

  const rows: readonly ReportRow[] = useMemo(
    () =>
      (roster.data?.data ?? [])
        .map((day) => ({
          day,
          presence: census.byEmployee.get(day.employee_id) ?? 'no_activity_data',
          totals: fold.perEmployee.get(day.employee_id),
          running: runningByEmployee.get(day.employee_id),
        }))
        .sort((a, b) => needRank(a) - needRank(b)),
    [roster.data, census, fold, runningByEmployee],
  );

  const groups: Readonly<Record<ExceptionKind, readonly ReportRow['day'][]>> = useMemo(
    () => ({
      late: rows
        .filter(
          (row) =>
            row.day.attendance_status === 'late' || row.day.attendance_status === 'late_and_early',
        )
        .map((row) => row.day),
      absent: rows.filter((row) => row.day.attendance_status === 'absent').map((row) => row.day),
      noActivity: rows.filter((row) => row.presence === 'no_activity_data').map((row) => row.day),
      unassigned: rows.filter((row) => row.running === undefined).map((row) => row.day),
    }),
    [rows],
  );

  const drillTo = attendanceDrillDownPath({ date: today, departmentId: department });

  // "Last updated" is the newest *successful* read across the regions, so a failed
  // refresh cannot advance it. `DESIGN.md` §8 makes this line a requirement, and
  // `PRODUCT.md` §4's ninth row makes it two stamps rather than one.
  const fetchedAt = Math.max(
    roster.fetchedAt ?? 0,
    windowDays.fetchedAt ?? 0,
    timers.fetchedAt ?? 0,
    tasks.fetchedAt ?? 0,
  );
  const refreshing =
    roster.refreshing || windowDays.refreshing || timers.refreshing || tasks.refreshing;
  const stale = roster.staleAfterError || windowDays.staleAfterError;

  return (
    <div className="wp-daysheet">
      <DaySheetHead
        date={today}
        today={todayLocal}
        timezone={timezone}
        agentSyncAt={fetchedAt === 0 ? null : new Date(fetchedAt).toISOString()}
        achauSyncAt={newestCachedAt(tasks.data)}
        refreshing={refreshing}
        onDate={(next) => {
          setFilter({ date: next });
        }}
      />

      <FilterBar
        window={windowChoice}
        date={today}
        departmentId={departmentId}
        onWindow={(next) => {
          setFilter({ window: next });
        }}
        onDepartment={(next) => {
          setFilter({ departmentId: next });
        }}
        onRefresh={() => {
          setReloadToken((value) => value + 1);
        }}
      />

      {stale ? (
        <p className="wp-daysheet__warning" data-testid="k4-stale-after-error">
          {t('overview.staleAfterErrorShort')}
        </p>
      ) : null}

      {roster.error !== null && roster.data === null ? (
        <ApiErrorNote
          error={roster.error}
          onRetry={roster.reload}
          subject={t('overview.reports.subject')}
          testId="k4-roster-error"
        />
      ) : null}
      {windowDays.error !== null && windowDays.data === null ? (
        <ApiErrorNote
          error={windowDays.error}
          onRetry={windowDays.reload}
          subject={t('overview.hours.title')}
          testId="k4-days-error"
        />
      ) : null}

      {roster.status === 'loading' && windowDays.status === 'loading' ? (
        <Skeleton rows={4} variant="block" label={t('overview.loading')} testId="k4-skeleton" />
      ) : (
        <>
          {/* Row 2. Exceptions first, then the three figures across under them —
              the persona's reading order, not a tile grid above the work. The two
              were side by side until the figure column outran the exception list by
              a screenful of white (finish review, round 2, fix 1). */}
          <div className="wp-daysheet__body">
            <ExceptionList
              groups={groups}
              timersRead={timersRead}
              date={today}
              departmentId={departmentId}
            />

            <section
              className="wp-section wp-daysheet__figures"
              aria-labelledby="k4-figures-title"
              data-testid="k4-tiles"
            >
              <h2 className="wp-section__title" id="k4-figures-title">
                {t('overview.figures.title')}
              </h2>
              <PresenceTile census={census} hasDayRows={(todaySource?.data.length ?? 0) > 0} />
              <HoursTile fold={fold} limit={TIMELINE_PAGE_LIMIT} />
              <RatioTile fold={fold} />
            </section>
          </div>

          {/* Row 3. Who needs help, ordered by need. */}
          <ReportsTable
            rows={rows}
            total={roster.data?.total ?? rows.length}
            timersRead={timersRead}
            date={today}
            departmentId={departmentId}
          />

          {/* Below the fold, and only here: the one chart. */}
          <TrendChart fold={fold} days={range.days} drillTo={drillTo} />

          <div className="wp-daysheet__sources">
            <TaskDataTile response={tasks.data} timezone={timezone} />
            <DeviceStrip
              permitted={canReadDevices}
              census={
                devices.data === null
                  ? null
                  : livenessCensus(devices.data.data, devices.data.page.total)
              }
            />
          </div>

          <PendingSlots />

          {/* Task 57 wired this. It reads one insight and renders it with the very
              card /ai-insights uses, so the caveat travels with the claim onto the
              dashboard instead of being trimmed to fit a tile. */}
          <AiSlot permissions={permissions} from={range.from} to={range.to} />

          {/* The page's own footnotes: what this sheet is over, and how often it
              re-reads. Facts about the screen rather than about the company, so they
              sit at the end of the reading order instead of above the exceptions. */}
          <footer className="wp-daysheet__foot">
            <p className="wp-small wp-muted" data-testid="k4-scope">
              {permissions.includes('workforce.view_workspace')
                ? t('overview.scope.company')
                : t('overview.scope.department')}
              {departmentId === null
                ? ''
                : ` · ${t('overview.scope.filtered', { id: departmentId })}`}
            </p>
            <p className="wp-small wp-muted" data-testid="k4-poll-note">
              {t('overview.pollNote', {
                aggregate: AGGREGATE_POLL_MS / 60_000,
                live: LIVE_POLL_MS / 1000,
              })}
            </p>
          </footer>
        </>
      )}
    </div>
  );
}
